import { ingestEvents } from '@/atoms/stats'
import { usePrimaryPage } from '@/PageManager'
import { notificationListStyleAtom } from '@/atoms/preferences'
import { loadMoreTimeline, onNewEvent, subscribeTimeline } from '@/services/client.service'
import { TFeedSubRequest, TNotificationType } from '@/types'
import { NostrEvent } from '@nostr/tools/wasm'
import { Filter, matchFilter } from '@nostr/tools/filter'
import * as kinds from '@nostr/tools/kinds'
import { loadRelayList } from '@nostr/gadgets/lists'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import PullToRefresh from 'react-simple-pull-to-refresh'
import Tabs from '../Tabs'
import { NotificationItem } from './NotificationItem'
import { NotificationSkeleton } from './NotificationItem/Notification'
import { batchDebounce, isTouchDevice, now } from '@/lib/utils'
import { RefreshButton } from '../RefreshButton'
import { isMention, replyKinds } from '@/lib/notification'
import { useCurrentPubkey } from '@/atoms/session'
import { useAtomValue } from 'jotai'
import { lastReadNotificationTimeAtom } from '@/atoms/notifications'

const LIMIT = 100
const SHOW_COUNT = 30

// newest first, ties broken by id so copies of the same event end up adjacent
function comesBefore(a: NostrEvent, b: NostrEvent) {
  return a.created_at === b.created_at ? (a.id < b.id ? -1 : 1) : b.created_at - a.created_at
}

const NotificationList = forwardRef((_, ref) => {
  const { t } = useTranslation()
  const { current, display } = usePrimaryPage()
  const active = useMemo(() => current === 'notifications' && display, [current, display])
  const pubkey = useCurrentPubkey()
  const notificationListStyle = useAtomValue(notificationListStyleAtom)
  const [notificationType, setNotificationType] = useState<TNotificationType>('all')
  const [refreshCount, setRefreshCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState<NostrEvent[]>([])
  const [filteredNotifications, setFilteredNotifications] = useState<NostrEvent[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [showCount, setShowCount] = useState(SHOW_COUNT)
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const lastReadTime = useAtomValue(lastReadNotificationTimeAtom)
  const supportTouch = useMemo(() => isTouchDevice(), [])
  const topRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const filterGeneration = useRef(0)

  const filter = useMemo<Omit<Filter, 'since' | 'until'> | undefined>(() => {
    if (!pubkey) return

    let filterKinds: number[] = []
    switch (notificationType) {
      case 'mentions':
        filterKinds = [
          kinds.PublicMessage,
          kinds.ShortTextNote,
          kinds.Comment,
          kinds.VoiceComment,
          kinds.Poll
        ]
        break
      case 'reactions':
        filterKinds = [kinds.Reaction, kinds.Repost, kinds.GenericRepost, kinds.PollResponse]
        break
      default:
        filterKinds = [
          kinds.PublicMessage,
          kinds.ShortTextNote,
          kinds.Repost,
          kinds.GenericRepost,
          kinds.Reaction,
          kinds.Comment,
          kinds.PollResponse,
          kinds.VoiceComment,
          kinds.Poll
        ]
    }

    return {
      '#p': [pubkey],
      kinds: filterKinds
    }
  }, [pubkey, notificationType])

  // Filter events for mentions and all tabs
  useEffect(() => {
    // Reactions tab doesn't need filtering
    if (notificationType !== 'mentions' && notificationType !== 'all') {
      setFilteredNotifications(events)
      return
    }

    if (!pubkey) {
      setFilteredNotifications([])
      return
    }

    // Increment generation to cancel previous filtering operations
    filterGeneration.current += 1
    const currentGeneration = filterGeneration.current

    const filterEvents = async () => {
      const filtered: (NostrEvent | Promise<NostrEvent | undefined>)[] = []

      for (const event of events) {
        // For text-based kinds, check if it's a mention
        if ((replyKinds as number[]).includes(event.kind)) {
          filtered.push(
            isMention(event, pubkey).then((is) => {
              if (is) return event
            })
          )
        } else {
          // For reactions, reposts - always include in All tab
          if (notificationType === 'all') {
            filtered.push(event)
          }
        }
      }

      const results = (await Promise.all(filtered)).filter(
        (evt): evt is NonNullable<NostrEvent> => !!evt
      )

      // Only apply results if this is still the current generation
      if (currentGeneration === filterGeneration.current) {
        setFilteredNotifications(results)
      }
    }

    filterEvents()
  }, [events, notificationType, pubkey])

  useEffect(() => {
    if (!pubkey || !filter) return

    // start with local DB immediately, no waiting for relay list fetch
    setSubRequests([{ source: 'local', filter }])
    ;(async () => {
      const { items } = await loadRelayList(pubkey)

      setSubRequests([
        {
          source: 'relays',
          urls: items
            .filter((r) => r.read)
            .map((r) => r.url)
            .slice(0, 3),
          filter
        },
        { source: 'local', filter }
      ])
    })()
  }, [pubkey, filter])

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        if (loading) return
        setRefreshCount((count) => count + 1)
      }
    }),
    [loading]
  )

  const handleNewEvent = batchDebounce(
    (newEvents: NostrEvent[]) => {
      newEvents.sort(comesBefore)
      ingestEvents(newEvents)

      setEvents((oldEvents) => {
        const merged: NostrEvent[] = []

        let j = 0 // pointer into oldEvents
        let prevNew: NostrEvent | undefined
        for (let i = 0; i < newEvents.length; i++) {
          const newEvent = newEvents[i]

          // the same event may have arrived through more than one source in this batch,
          // since newEvents is sorted the copies will be adjacent
          if (prevNew && prevNew.id === newEvent.id) continue
          prevNew = newEvent

          // emit all old events that are strictly newer
          while (j < oldEvents.length && oldEvents[j].created_at > newEvent.created_at) {
            merged.push(oldEvents[j])
            j++
          }

          // a duplicate in oldEvents can only be among those with the exact same created_at
          let exists = false
          for (
            let k = j;
            k < oldEvents.length && oldEvents[k].created_at === newEvent.created_at;
            k++
          ) {
            if (oldEvents[k].id === newEvent.id) {
              exists = true
              break
            }
          }
          if (exists) continue

          merged.push(newEvent)
        }

        // whatever is left in oldEvents is older than everything we got
        for (; j < oldEvents.length; j++) {
          merged.push(oldEvents[j])
        }

        return merged
      })
    },
    1800,
    3000
  )

  useEffect(() => {
    if (!subRequests || !pubkey) return
    if (current !== 'notifications') return

    // cancel any pending debounced events from previous subscription
    handleNewEvent.cancel()

    setLoading(true)
    setEvents([])
    setShowCount(SHOW_COUNT)

    const subc = subscribeTimeline(
      subRequests,
      { limit: LIMIT },
      {
        onEvents: (events, isFinal) => {
          setEvents(events)

          if (isFinal) {
            setHasMore(events.length > 0)
            setLoading(false)
            ingestEvents(events)
          }
        },
        onNew: handleNewEvent
      }
    )

    return () => subc.close()
  }, [pubkey, refreshCount, current, subRequests])

  useEffect(() => {
    if (!active || !pubkey || !filter) return

    function handler(evt: NostrEvent) {
      if (matchFilter(filter!, evt)) {
        handleNewEvent(evt)
      }
    }

    return onNewEvent(handler)
  }, [pubkey, active, filter])

  const loadingRef = useRef(false)
  const eventsRef = useRef<NostrEvent[]>([])
  const showCountRef = useRef(SHOW_COUNT)
  const filteredNotificationsRef = useRef<NostrEvent[]>([])
  const subRequestsRef = useRef<null | TFeedSubRequest[]>([])
  const pubkeyRef = useRef<string | null>(pubkey)
  const loadMoreRef = useRef<() => Promise<void>>(async () => {})

  useEffect(() => {
    eventsRef.current = events
  }, [events])

  useEffect(() => {
    showCountRef.current = showCount
  }, [showCount])

  useEffect(() => {
    filteredNotificationsRef.current = filteredNotifications
  }, [filteredNotifications])

  useEffect(() => {
    subRequestsRef.current = subRequests
  }, [subRequests])

  useEffect(() => {
    pubkeyRef.current = pubkey
  }, [pubkey])

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return

    const currentPubkey = pubkeyRef.current
    const currentSubRequests = subRequestsRef.current
    const currentEvents = eventsRef.current
    const currentShowCount = showCountRef.current
    const currentFiltered = filteredNotificationsRef.current

    if (!currentPubkey || !currentSubRequests || currentSubRequests.length === 0) return

    if (currentShowCount < currentEvents.length) {
      setShowCount((count) => count + SHOW_COUNT)
      if (currentFiltered.length - currentShowCount > LIMIT / 2) {
        return
      }
    }

    loadingRef.current = true
    setLoading(true)
    try {
      const olderEvents = await loadMoreTimeline(currentSubRequests, {
        until:
          currentEvents.length > 0 ? currentEvents[currentEvents.length - 1].created_at - 1 : now(),
        limit: LIMIT
      })

      if (olderEvents.length > 0) {
        setEvents((currentEvents) => {
          const existingIds = new Set(currentEvents.map((e) => e.id))
          const seenInBatch = new Set<string>()
          const newEvents = olderEvents.filter((event) => {
            if (event.pubkey === currentPubkey) return false
            if (existingIds.has(event.id)) return false
            if (seenInBatch.has(event.id)) return false
            seenInBatch.add(event.id)
            return true
          })
          return [...currentEvents, ...newEvents]
        })
      } else {
        setHasMore(false)
      }
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  loadMoreRef.current = loadMore

  useEffect(() => {
    if (!hasMore || loading) return

    const observerInstance = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current()
        }
      },
      {
        root: null,
        rootMargin: '10px',
        threshold: 1
      }
    )

    const currentBottomRef = bottomRef.current

    if (currentBottomRef) {
      observerInstance.observe(currentBottomRef)
    }

    return () => {
      if (observerInstance && currentBottomRef) {
        observerInstance.unobserve(currentBottomRef)
      }
    }
  }, [hasMore, loading])

  const refresh = () => {
    topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' })
    setTimeout(() => {
      setRefreshCount((count) => count + 1)
    }, 500)
  }

  const list = (
    <div className={notificationListStyle === 'compact' ? 'pt-2' : ''}>
      {filteredNotifications.slice(0, showCount).map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          isNew={notification.created_at > lastReadTime}
        />
      ))}
      <div className="text-center text-sm text-muted-foreground">
        {hasMore || loading ? (
          <div ref={bottomRef}>
            <NotificationSkeleton />
          </div>
        ) : (
          t('no more notifications')
        )}
      </div>
    </div>
  )

  const tabs = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'mentions', label: 'Mentions' },
      { value: 'reactions', label: 'Reactions' }
    ],
    []
  )

  return (
    <div>
      <Tabs
        value={notificationType}
        tabs={tabs}
        onTabChange={(type) => {
          setShowCount(SHOW_COUNT)
          setNotificationType(type as TNotificationType)
        }}
        options={!supportTouch ? <RefreshButton onClick={() => refresh()} /> : null}
      />
      <div ref={topRef} className="scroll-mt-[calc(6rem+1px)]" />
      {supportTouch ? (
        <PullToRefresh
          onRefresh={async () => {
            refresh()
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }}
          pullingContent=""
        >
          {list}
        </PullToRefresh>
      ) : (
        list
      )}
    </div>
  )
})

NotificationList.displayName = 'NotificationList'

export default NotificationList
